import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Menu } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useEffect } from 'react';
import generatePath from '~settings/routing';
import {
  Property as PropertyIcon,
  IconDashboard as DashboardIcon,
  NavIconBookings as NavIconBookingsIcon,
  Billing as BillingIcon,
  NavIconPayment as NavIconPaymentIcon,
  AccountIcon as AccountIconIcon,
} from '~components/svgs';

const useStyles = makeStyles(
  theme => ({
    root: {
      '& .ant-menu-submenu-title': {
        ...theme.typography.body2,
        height: '44px !important',
        fontFamily: theme.typography.fontFamilySmall,
        position: 'relative',
        lineHeight: '16px !important',
        margin: '0 !important',
        padding: '12px 24px !important',
        paddingLeft: '54PX !important',
        backgroundColor: '#4C4C4C',
      },

      '& .ant-menu-submenu, .ant-menu-submenu-vertical': {
        backgroundColor: '#4C4C4C !important',
      },

      '& .ant-menu-item-active, .ant-menu-submenu-active': {
        color: theme.colors.gray1,
        backgroundColor: '#4C4C4C !important',

        '& svg': {
          fill: theme.colors.gray1,
        },
      },

      '& .ant-menu-item-selected': {
        color: theme.colors.gray1,
        backgroundColor: '#58595B !important',

        '& svg': {
          fill: '#fcce01 !important',
        },
      },

      '& .ant-menu-submenu': {
        backgroundColor: '#4C4C4C',
      },

      '& ul.ant-menu.ant-menu-sub': {
        backgroundColor: '#4c4c4c',
        boxShadow: 'none',
      },

      '& .ant-menu-submenu-open': {
        '& svg': {
          fill: theme.colors.gray1,
        },
      },

      '.ant-menu-inline-collapsed': {
        '& > svg': {
          marginTop: 10,
        },
      },
    },
    item: {
      height: '44px !important',
      margin: '0 !important',
      padding: '12px 24px !important',
      backgroundColor: '#4C4C4C',
    },
    subItem: {
      paddingLeft: '24px !important',
      backgroundColor: 'rgba(37, 37, 37, 0.2)',
    },
    collapsedSubItem: {
      paddingLeft: '54px !important',
      backgroundColor: 'rgba(37, 37, 37, 0.2)',
    },
    sidebarIcon: ({ collapsed }) => ({
      width: 18,
      height: 16,
      marginRight: 12,
      fill: '#9E9E9E',
      verticalAlign: 'sub',
      marginTop: collapsed ? '10px' : 0,
    }),
    subMenu: {
      height: '44px !important',
      '& > svg': {
        position: 'absolute',
        top: 11,
        left: 24,
        height: 18,
      },
      '& > span': {
        lineHeight: '16px',
        fontWeight: 600,
      },
    },
    subMenuCollapsed: {
      display: 'inherit',

      '& > svg': {
        position: 'inherit',
      },
    },
    link: {
      ...theme.typography.body2,
      fontFamily: theme.typography.fontFamilySmall,
      '& > span': {
        fontWeight: 600,
      },
    },
    subMenuSelected: {
      '& > span': {
        color: theme.colors.gray1,
      },

      '& > svg': {
        fill: '#fcce01 !important',
      },
    },
  }),
  { name: 'MenuList' },
);

const MenuItem = ({
  menuKey,
  title,
  icon,
  link,
  text,
  collapsed,
  className,
  isSubMenuItem,
  ...otherProps
}) => {
  const classes = useStyles();

  return (
    <Menu.Item
      key={menuKey}
      className={classNames(classes.item, className)}
      title={title}
      {...otherProps}
    >
      <Link
        to={{
          pathname: link,
          state: {
            from: 'menu',
          },
        }}
        className={classes.link}
      >
        {icon}
        <If condition={!collapsed || isSubMenuItem}>
          <span className={classes.text}>{text}</span>
        </If>
      </Link>
    </Menu.Item>
  );
};

MenuItem.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool.isRequired,
  icon: PropTypes.object,
  link: PropTypes.string.isRequired,
  menuKey: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  isSubMenuItem: PropTypes.bool,
};

const SubMenuItems = ({
  menuKey,
  className,
  collapsed,
  icon,
  title,
  subMenuData,
  ...otherProps
}) => {
  const classes = useStyles();

  return (
    <Menu.SubMenu
      id="area"
      {...otherProps}
      key={menuKey}
      title={
        <div className={className}>
          {icon}
          <If condition={!collapsed}>
            <span style={{ width: '100%' }}>{title}</span>
          </If>
        </div>
      }
    >
      <For of={subMenuData} each="item">
        <MenuItem
          isSubMenuItem={true}
          {...item}
          menuKey={item?.key}
          collapsed={collapsed}
          popupClassName={classes.item}
          className={collapsed ? classes.subItem : classes.collapsedSubItem}
        />
      </For>
    </Menu.SubMenu>
  );
};

SubMenuItems.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool.isRequired,
  icon: PropTypes.object,
  menuKey: PropTypes.string.isRequired,
  subMenuData: PropTypes.array.isRequired,
  text: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const MenuList = ({ collapsed, routeProps }) => {
  const { t } = useTranslation();
  const classes = useStyles({ collapsed: collapsed });

  const [subOpenKeys, setSubOpenKeys] = useState();

  const {
    hasHerculesProperties,
    hasManualBookings,
    hasNoPaymentHerculesBookings,
    hasNoPaymentInstantBookings,
    hasInstantBookProperties,
    onlyHasLeadProperties,
  } = useSelector(state => ({
    hasManualProperties: state.auth.getIn(['properties', 'hasManualProperties']),
    hasHerculesProperties: state.auth.getIn(['properties', 'hasHerculesProperties']),
    hasYugoProperties: state.auth.getIn(['properties', 'hasYugoProperties']),
    hasManualBookings: state.auth.getIn(['properties', 'hasManualBookings']),
    hasNoPaymentHerculesBookings: state.auth.getIn(['properties', 'hasNoPaymentHerculesBookings']),
    hasNoPaymentInstantBookings: state.auth.getIn(['properties', 'hasNoPaymentInstantBookings']),
    hasInstantBookProperties: state.auth.getIn(['properties', 'hasInstantBookProperties']),
    onlyHasLeadProperties: state.auth.getIn(['properties', 'onlyHasLeadProperties']),
  }));

  const menuOptions = useMemo(
    () => [
      {
        key: 'board',
        link: '/board',
        title: t('cms:cms.sidebar.menu.dashboard'),
        icon: <DashboardIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.dashboard'),
      },
      {
        key: 'properties',
        link: '/properties',
        title: t('cms:cms.sidebar.menu.properties'),
        icon: <PropertyIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.properties'),
      },
      {
        key: 'orders',
        link: generatePath('orders.list', {}),
        title: t('cms:cms.sidebar.menu.bookings'),
        icon: <NavIconBookingsIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.bookings'),
        disabled: onlyHasLeadProperties,
      },
      {
        key: 'reconciliation',
        link: generatePath('reconciliation'),
        title: t('cms:cms.sidebar.menu.reconciliation'),
        icon: <BillingIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.reconciliation'),
        disabled:
          !hasManualBookings && !hasNoPaymentHerculesBookings && !hasNoPaymentInstantBookings,
      },
      {
        key: 'payment',
        title: t('cms:cms.sidebar.menu.payment'),
        icon: <NavIconPaymentIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.payment'),
        disabled: (!hasHerculesProperties && !hasInstantBookProperties) || onlyHasLeadProperties,
        submenu: [
          {
            key: 'paymentPlans',
            link: generatePath('paymentList'),
            title: t('cms:cms.sidebar.menu.payment_plans'),
            text: t('cms:cms.sidebar.menu.payment_plans'),
          },
          {
            key: 'withdrawal',
            link: generatePath('withdrawal'),
            title: t('cms:cms.sidebar.menu.payment_method'),
            text: t('cms:cms.sidebar.menu.payment_method'),
          },
        ],
      },
      {
        key: 'account',
        title: t('cms:cms.sidebar.menu.account'),
        icon: <AccountIconIcon className={classes.sidebarIcon} />,
        text: t('cms:cms.sidebar.menu.account'),
        submenu: [
          {
            key: 'profile',
            link: generatePath('accountProfile'),
            title: t('my_profile'),
            text: t('my_profile'),
          },
          {
            key: 'security',
            link: generatePath('loginSecurity'),
            title: t('account_login_security_menu'),
            text: t('account_login_security_menu'),
          },
        ],
      },
    ],
    [
      t,
      classes.sidebarIcon,
      onlyHasLeadProperties,
      hasManualBookings,
      hasNoPaymentHerculesBookings,
      hasNoPaymentInstantBookings,
      hasHerculesProperties,
      hasInstantBookProperties,
    ],
  );

  const selectTab = useMemo(() => {
    const { pathname } = routeProps.location;

    if (pathname.includes('/properties') || pathname.includes('/property')) {
      return ['properties'];
    }

    if (pathname.includes('payment')) {
      setSubOpenKeys(['payment', 'paymentPlans']);
      return ['payment', 'paymentPlans'];
    }

    if (pathname.includes('withdrawal')) {
      setSubOpenKeys(['payment', 'withdrawal']);
      return ['payment', 'withdrawal'];
    }

    if (pathname.includes('onboarding')) {
      return ['onboarding'];
    }

    if (pathname.includes('reconciliation')) {
      return ['reconciliation'];
    }

    if (pathname.includes('booking')) {
      return ['orders'];
    }

    if (pathname.includes('board')) {
      return ['board'];
    }

    if (pathname.includes('profile')) {
      setSubOpenKeys(['account', 'profile']);
      return ['account', 'profile'];
    }

    if (pathname.includes('security')) {
      setSubOpenKeys(['account', 'security']);
      return ['account', 'security'];
    }

    return ['board'];
  }, [routeProps]);

  const onOpenChange = openKeys => {
    setSubOpenKeys(openKeys);
  };

  useEffect(() => {
    const needOpenSubmenuKeys = ['account', 'payment'];
    if (!selectTab.some(tabKey => needOpenSubmenuKeys.includes(tabKey))) {
      setSubOpenKeys([]);
    }
  }, [selectTab]);

  return (
    <div>
      <Menu
        getPopupContainer={trigger => trigger.parentElement}
        theme="dark"
        mode={collapsed ? 'vertical' : 'inline'}
        selectedKeys={selectTab}
        inlineCollapsed={collapsed}
        openKeys={subOpenKeys}
        onOpenChange={onOpenChange}
        className={classes.root}
      >
        <For of={menuOptions} each="option">
          <If condition={!option.disabled}>
            <Choose>
              <When condition={option.submenu}>
                <SubMenuItems
                  {...option}
                  menuKey={option?.key}
                  collapsed={collapsed}
                  className={classNames(classes.subMenu, {
                    [classes.subMenuSelected]: selectTab.includes(option.key),
                    [classes.subMenuCollapsed]: collapsed,
                  })}
                  subMenuData={option.submenu}
                />
              </When>
              <Otherwise>
                <MenuItem {...option} menuKey={option?.key} collapsed={collapsed} />
              </Otherwise>
            </Choose>
          </If>
        </For>
      </Menu>
    </div>
  );
};

MenuList.propTypes = {
  collapsed: PropTypes.bool,
  routeProps: PropTypes.object.isRequired,
};

export default MenuList;
